import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const PlayerCtx = createContext(null);

let apiLoaded = null;
export function loadYT() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (apiLoaded) return apiLoaded;
  apiLoaded = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT);
    };
  });
  return apiLoaded;
}

export function PersistentPlayerProvider({ children }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const listenersRef = useRef(new Set());
  const pendingVideoRef = useRef(null);
  const wantsPlayRef = useRef(false);
  const wantsUnmuteRef = useRef(false);

  useEffect(() => {
    let destroyed = false;
    loadYT().then((YT) => {
      if (destroyed || !containerRef.current) return;
      playerRef.current = new YT.Player(containerRef.current, {
        height: "180",
        width: "320",
        videoId: "",
        playerVars: {
          autoplay: 0,
          controls: 1,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
        },
        events: {
          onReady: () => {
            setReady(true);
            // Flush any queued intents (from clicks that happened before YT loaded)
            if (wantsUnmuteRef.current) {
              try { playerRef.current.unMute(); } catch {}
              wantsUnmuteRef.current = false;
            }
            if (pendingVideoRef.current) {
              try { playerRef.current.loadVideoById(pendingVideoRef.current); } catch {}
              pendingVideoRef.current = null;
            } else if (wantsPlayRef.current) {
              try { playerRef.current.playVideo(); } catch {}
              wantsPlayRef.current = false;
            }
          },
          onStateChange: (e) => {
            setPlaying(e.data === 1);
            listenersRef.current.forEach((cb) => { try { cb(e.data); } catch {} });
          },
        },
      });
    });
    return () => {
      destroyed = true;
      try { playerRef.current?.destroy(); } catch {}
      playerRef.current = null;
    };
  }, []);

  const api = {
    ready,
    playing,
    load: (videoId) => {
      if (!videoId) return;
      if (!playerRef.current || !ready) {
        pendingVideoRef.current = videoId;
        return;
      }
      try { playerRef.current.loadVideoById(videoId); } catch {}
    },
    play: () => {
      if (!playerRef.current || !ready) { wantsPlayRef.current = true; return; }
      try { playerRef.current.playVideo(); } catch {}
    },
    pause: () => { try { playerRef.current?.pauseVideo(); } catch {} },
    stop: () => { try { playerRef.current?.stopVideo(); } catch {} },
    unmute: () => {
      if (!playerRef.current || !ready) { wantsUnmuteRef.current = true; return; }
      try { playerRef.current.unMute(); } catch {}
    },
    mute: () => { try { playerRef.current?.mute(); } catch {} },
    onStateChange: (cb) => {
      listenersRef.current.add(cb);
      return () => listenersRef.current.delete(cb);
    },
    getIframe: () => playerRef.current?.getIframe?.(),
  };

  return (
    <PlayerCtx.Provider value={api}>
      {/* Persistent hidden player - lives across route changes so user-gesture-triggered playback isn't lost on navigation */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          width: 320,
          height: 180,
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      >
        <div ref={containerRef} />
      </div>
      {children}
    </PlayerCtx.Provider>
  );
}

export const usePersistentPlayer = () => useContext(PlayerCtx);
